// src/utils/generateInvoice.js

/**
 * Generates and downloads a PDF invoice for an order.
 * Uses browser's print-to-PDF method (no external library needed).
 */
export const generateInvoice = (order, siteSettings = {}) => {
  if (!order) {
    alert('Order not found');
    return;
  }

  const siteName = siteSettings.siteName || 'Organic Land';
  const sitePhone = siteSettings.phone || '+92 300 1234567';
  const siteEmail = siteSettings.email || 'hello@organicland.com';
  const siteAddress = siteSettings.address || 'Lahore, Pakistan';
  const siteLogo = siteSettings.logo || '';

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString('en-PK', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return '-'; }
  };

  const orderShortId = order.id?.slice(-8).toUpperCase() || 'N/A';

  const itemsHTML = (order.items || []).map(item => `
    <tr>
      <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">
        <div style="display: flex; align-items: center; gap: 10px;">
          ${item.image ? `<img src="${item.image}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 6px;" />` : ''}
          <span style="font-weight: 600; color: #1f2937;">${item.title || 'Item'}</span>
        </div>
      </td>
      <td style="padding: 10px; text-align: center; color: #6b7280;">${item.quantity}</td>
      <td style="padding: 10px; text-align: right; color: #6b7280;">Rs. ${item.price}</td>
      <td style="padding: 10px; text-align: right; font-weight: 700; color: #16a34a;">Rs. ${item.price * item.quantity}</td>
    </tr>
  `).join('');

  const discountRow = order.discount > 0
    ? `<tr><td colspan="3" style="padding: 5px 10px; text-align: right; color: #16a34a; font-weight: 600;">Discount ${order.couponCode ? `(${order.couponCode})` : ''}:</td><td style="padding: 5px 10px; text-align: right; font-weight: 600; color: #16a34a;">- Rs. ${order.discount}</td></tr>`
    : '';

  const shippingRow = order.shipping > 0
    ? `<tr><td colspan="3" style="padding: 5px 10px; text-align: right; color: #6b7280;">Shipping:</td><td style="padding: 5px 10px; text-align: right; font-weight: 600; color: #1f2937;">Rs. ${order.shipping}</td></tr>`
    : `<tr><td colspan="3" style="padding: 5px 10px; text-align: right; color: #6b7280;">Shipping:</td><td style="padding: 5px 10px; text-align: right; font-weight: 600; color: #16a34a;">FREE</td></tr>`;

  const taxRow = order.tax > 0
    ? `<tr><td colspan="3" style="padding: 5px 10px; text-align: right; color: #6b7280;">${order.taxName || 'Tax'} ${order.taxRate ? `(${order.taxRate}%)` : ''}:</td><td style="padding: 5px 10px; text-align: right; font-weight: 600; color: #1f2937;">Rs. ${order.tax}</td></tr>`
    : '';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8" />
      <title>Invoice - ${orderShortId}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: #f9fafb;
          padding: 40px 20px;
          color: #1f2937;
        }
        .invoice {
          max-width: 800px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
        }
        .header {
          background: linear-gradient(135deg, #16a34a 0%, #14532d 100%);
          color: #fff;
          padding: 30px 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
        }
        .header h1 { font-size: 28px; font-weight: 700; margin-bottom: 5px; }
        .header p { font-size: 13px; opacity: 0.9; }
        .logo { font-size: 22px; font-weight: 700; }
        .logo img { max-height: 50px; max-width: 150px; object-fit: contain; }
        .meta {
          display: flex;
          justify-content: space-between;
          padding: 25px 40px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 15px;
        }
        .meta-item h3 {
          font-size: 11px;
          text-transform: uppercase;
          color: #6b7280;
          letter-spacing: 1px;
          margin-bottom: 5px;
          font-weight: 600;
        }
        .meta-item p { font-size: 14px; font-weight: 600; color: #1f2937; }
        .section { padding: 25px 40px; }
        .section h2 {
          font-size: 16px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 15px;
          padding-bottom: 8px;
          border-bottom: 2px solid #16a34a;
          display: inline-block;
        }
        .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .two-col p { font-size: 13px; color: #4b5563; line-height: 1.6; }
        .two-col strong { color: #1f2937; display: block; margin-bottom: 4px; font-size: 12px; }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          font-size: 13px;
        }
        table thead {
          background: #f9fafb;
        }
        table thead th {
          padding: 12px 10px;
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          color: #6b7280;
          letter-spacing: 1px;
          font-weight: 700;
          border-bottom: 2px solid #e5e7eb;
        }
        table thead th:nth-child(2),
        table thead th:nth-child(3),
        table thead th:nth-child(4) { text-align: right; }
        table thead th:nth-child(2) { text-align: center; }
        .totals {
          padding: 0 40px 25px;
        }
        .totals table {
          width: 100%;
          max-width: 350px;
          margin-left: auto;
          margin-top: 15px;
        }
        .totals .grand-total {
          font-size: 16px;
          font-weight: 700;
          color: #16a34a;
          padding: 12px 10px;
          border-top: 2px solid #16a34a;
        }
        .footer {
          background: #14532d;
          color: #fff;
          padding: 20px 40px;
          text-align: center;
          font-size: 12px;
        }
        .footer p { margin-bottom: 4px; opacity: 0.9; }
        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }
        .badge-paid { background: #d1fae5; color: #065f46; }
        .badge-unpaid { background: #fee2e2; color: #991b1b; }
        .badge-pending { background: #fef3c7; color: #92400e; }
        @media print {
          body { background: #fff; padding: 0; }
          .invoice { box-shadow: none; border-radius: 0; }
          .no-print { display: none; }
        }
        .print-btn {
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #16a34a 0%, #14532d 100%);
          color: #fff;
          border: none;
          padding: 12px 24px;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 10px 30px rgba(22, 163, 74, 0.4);
          display: flex;
          align-items: center;
          gap: 8px;
          z-index: 1000;
        }
        .print-btn:hover { transform: translateY(-2px); }
        @media print {
          .print-btn { display: none; }
        }
      </style>
    </head>
    <body>
      <button class="print-btn no-print" onclick="window.print()">
        🖨️ Save as PDF / Print
      </button>

      <div class="invoice">

        <!-- Header -->
        <div class="header">
          <div>
            <div class="logo">
              ${siteLogo
                ? `<img src="${siteLogo}" alt="${siteName}" />`
                : `🌿 ${siteName}`}
            </div>
          </div>
          <div style="text-align: right;">
            <h1>INVOICE</h1>
            <p>#${orderShortId}</p>
          </div>
        </div>

        <!-- Meta -->
        <div class="meta">
          <div class="meta-item">
            <h3>Invoice Date</h3>
            <p>${formatDate(order.createdAt)}</p>
          </div>
          <div class="meta-item">
            <h3>Order Status</h3>
            <p style="text-transform: capitalize;">${order.status || 'Processing'}</p>
          </div>
          <div class="meta-item">
            <h3>Payment</h3>
            <span class="badge ${
              order.paymentStatus === 'paid' ? 'badge-paid' :
              order.paymentStatus === 'pending-verification' ? 'badge-pending' : 'badge-unpaid'
            }">
              ${order.paymentStatus === 'paid' ? 'PAID' :
                order.paymentStatus === 'pending-verification' ? 'PENDING' : 'UNPAID'}
            </span>
          </div>
        </div>

        <!-- Bill To / Ship To -->
        <div class="section">
          <div class="two-col">
            <div>
              <h2>BILL TO</h2>
              <p>
                <strong>${order.customerName || '-'}</strong>
                ${order.customerEmail || ''}<br />
                ${order.phone || ''}<br />
                ${order.address || ''}<br />
                ${order.city || ''}${order.state ? `, ${order.state}` : ''}${order.postalCode ? ` - ${order.postalCode}` : ''}
              </p>
            </div>
            <div>
              <h2>FROM</h2>
              <p>
                <strong>${siteName}</strong>
                ${siteEmail}<br />
                ${sitePhone}<br />
                ${siteAddress}
              </p>
            </div>
          </div>
        </div>

        <!-- Items -->
        <div class="section" style="padding-top: 0;">
          <h2>ORDER ITEMS</h2>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHTML}
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="totals">
          <table>
            <tr>
              <td colspan="3" style="padding: 5px 10px; text-align: right; color: #6b7280;">Subtotal:</td>
              <td style="padding: 5px 10px; text-align: right; font-weight: 600;">Rs. ${order.subtotal || 0}</td>
            </tr>
            ${discountRow}
            ${shippingRow}
            ${taxRow}
            <tr>
              <td colspan="3" class="grand-total" style="text-align: right;">Grand Total:</td>
              <td class="grand-total" style="text-align: right;">Rs. ${order.total || 0}</td>
            </tr>
          </table>
        </div>

        <!-- Footer -->
        <div class="footer">
          <p><strong>Thank you for your order!</strong></p>
          <p>${siteName} • ${sitePhone} • ${siteEmail}</p>
          <p style="margin-top: 10px; font-size: 11px; opacity: 0.7;">
            This is a computer-generated invoice. No signature required.
          </p>
        </div>

      </div>
    </body>
    </html>
  `;

  // Open in new window
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Kripya popup block allow karein.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
};

export default generateInvoice;